import * as grpc from "grpc-web";
import {
  questionService,
  responseService,
  sectionService,
  surveyService,
} from "../globalClient/GlobalClients";
import {
  CreateResponseRequest,
  CreateResponseResponse,
  CreateSurveyRequest,
  CreateSurveyResponse,
  DeleteQuestionRequest,
  DeleteQuestionResponse,
  DeleteSectionRequest,
  DeleteSectionResponse,
  DeleteSurveyRequest,
  DeleteSurveyResponse,
  GetAnalyticsRequest,
  GetAnalyticsResponse,
  GetResponseCSVRequest,
  GetResponseCSVResponse,
  Question,
  ReadResponseResponse,
  ReadSurveyListRequest,
  ReadSurveyListResponse,
  ReadSurveyRequest,
  ReadSurveyResponse,
  Response,
  ResponseListRequest,
  ResponseListResponse,
  Survey,
  UpdateQuestionRequest,
  UpdateQuestionResponse,
} from "../pb/surveys_pb";
import {
  formatQuestionJsonToQuestionClasses,
  formatResponseJsonToResponseClass,
  formatSurveyJsonToSurveyClass,
} from "./SurveyUtils";

export const getSurveyList = async (
  org: string
): Promise<ReadSurveyListResponse.AsObject> => {
  const request = new ReadSurveyListRequest();
  request.setOrganisation(org);
  const res = new Promise<ReadSurveyListResponse.AsObject>(
    (resolve, reject) => {
      surveyService.readSurveyList(
        request,
        {
          "Content-Type": "application/grpc-web-text+proto",
        },
        (err: grpc.RpcError, response: ReadSurveyListResponse) => {
          if (err) {
            reject(err);
          }
          resolve(response?.toObject());
        }
      );
    }
  );
  return res;
};

export const createSurvey = async (
  data: Survey.AsObject
): Promise<CreateSurveyResponse.AsObject> => {
  const request = new CreateSurveyRequest();
  request.setSurvey(formatSurveyJsonToSurveyClass(data));
  const res = new Promise<CreateSurveyResponse.AsObject>((resolve, reject) => {
    surveyService.createSurvey(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: CreateSurveyResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response?.toObject());
      }
    );
  });
  return res;
};

export const getSurveyById = async (
  id: string
): Promise<ReadSurveyResponse.AsObject> => {
  const request = new ReadSurveyRequest();
  request.setId(id);
  const res = new Promise<ReadSurveyResponse.AsObject>((resolve, reject) => {
    surveyService.readSurvey(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: ReadSurveyResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response?.toObject());
      }
    );
  });
  return res;
};

export const createSurveyResponse = async (
  data: Response.AsObject
): Promise<CreateResponseResponse.AsObject> => {
  const request = new CreateResponseRequest();

  request.setResponse(formatResponseJsonToResponseClass(data));
  const res = new Promise<CreateResponseResponse.AsObject>(
    (resolve, reject) => {
      responseService.createResponse(
        // Request
        request,
        // Metadata
        {
          "Content-Type": "application/grpc-web-text+proto",
        },
        // Callback
        (err: grpc.RpcError, response: CreateResponseResponse) => {
          if (err) {
            reject(err);
          }
          resolve(response?.toObject());
        }
      );
    }
  );
  return res;
};

export const getResponseById = async (
  id: string
): Promise<ReadResponseResponse.AsObject> => {
  const request = new ReadSurveyRequest();
  request.setId(id);
  const res = new Promise<ReadSurveyResponse.AsObject>((resolve, reject) => {
    responseService.readResponse(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: ReadResponseResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response?.toObject());
      }
    );
  });
  return res;
};

export const getResponseBySurveyId = async (
  surveyId: string
): Promise<ResponseListResponse.AsObject> => {
  const request = new ResponseListRequest();
  request.setSurveyId(surveyId);
  const res = new Promise<ResponseListResponse.AsObject>((resolve, reject) => {
    responseService.readResponseList(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: ResponseListResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response?.toObject());
      }
    );
  });
  return res;
};

export const getResponseAnalytics = async (
  surveyId: string
): Promise<GetAnalyticsResponse.AsObject> => {
  let request = new GetAnalyticsRequest();
  request.setSurveyId(surveyId);
  const res = new Promise<GetAnalyticsResponse.AsObject>((resolve, reject) => {
    responseService.readResponseAnalytics(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: GetAnalyticsResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response?.toObject());
      }
    );
  });
  return res;
};

export const getCSVStream = async (
  surveyId: string,
  serialNumber: string
): Promise<GetResponseCSVResponse[]> => {
  return new Promise<GetResponseCSVResponse[]>((resolve, reject) => {
    let req = new GetResponseCSVRequest();
    req.setSurveyId(surveyId);
    const res = responseService.readResponseCSV(req, {
      "Content-Type": "application/grpc-web-text+proto",
    });
    const responseArray: GetResponseCSVResponse[] = [];
    res.on("data", (chunk: GetResponseCSVResponse) => {
      responseArray.push(chunk);
    });
    res.on("end", () => {
      resolve(responseArray);
    });
    res.on("error", (error) => {
      reject(error);
    });
  });
};

export const deleteSurvey = (
  surveyId: string
): Promise<DeleteSurveyResponse> => {
  const request = new DeleteSurveyRequest();
  request.setId(surveyId);
  const res = new Promise<DeleteSurveyResponse>((resolve, reject) => {
    surveyService.deleteSurvey(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: DeleteSurveyResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response);
      }
    );
  });
  return res;
};
export const deleteQuestion = (
  questionId: string
): Promise<DeleteQuestionResponse> => {
  const request = new DeleteQuestionRequest();
  request.setId(questionId);
  const res = new Promise<DeleteQuestionResponse>((resolve, reject) => {
    questionService.deleteQuestion(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: DeleteQuestionResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response);
      }
    );
  });
  return res;
};

export const updateQuestion = (
  data: Question.AsObject
): Promise<UpdateQuestionResponse> => {
  const request = new UpdateQuestionRequest();
  request.setQuestion(formatQuestionJsonToQuestionClasses([data])[0]);
  const res = new Promise<UpdateQuestionResponse>((resolve, reject) => {
    questionService.updateQuestion(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: UpdateQuestionResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response);
      }
    );
  });
  return res;
};

export const deleteSection = (
  sectionId: string
): Promise<DeleteQuestionResponse> => {
  let request = new DeleteSectionRequest();
  request.setId(sectionId);

  const res = new Promise<DeleteSectionResponse>((resolve, reject) => {
    sectionService.deleteSection(
      // Request
      request,
      // Metadata
      {
        "Content-Type": "application/grpc-web-text+proto",
      },
      // Callback
      (err: grpc.RpcError, response: DeleteSectionResponse) => {
        if (err) {
          reject(err);
        }
        resolve(response);
      }
    );
  });
  return res;
};
